import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '~/components/Button';
import { showToast } from '~/components/Toast';
import { SettingsPageHeader } from '~/modules/settings/components/SettingsPageHeader';
import { useLabelGroupCreate } from '~settings/api/mutations/labelGroupCreate';
import { CTABlock } from '~settings/components/CTABlock';
import { SETTINGS_ROUTES } from '~settings/config/routes';
import type { LabelGroupFormSchema } from '~settings/modules/label-group/components/LabelGroupForm';
import { LabelGroupForm } from '~settings/modules/label-group/components/LabelGroupForm';
import { getMessageFromStatus } from '~settings/modules/label-group/helpers';

const FORM_ID = 'createLabelGroupPage';

export function CreateLabelGroupPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();

  const { mutate: submitData, isPending } = useLabelGroupCreate({
    onSuccess: (data) => {
      showToast({
        title: t('labelGroup.createSuccessMessage', {
          name: data.name,
          interpolation: { escapeValue: false },
        }),
        type: 'success',
      });
      navigate(SETTINGS_ROUTES.LABEL_GROUP_DETAIL(data.id));
    },
    onError: (error) => {
      const status = error?.response?.status;
      const message = getMessageFromStatus(status, true);

      showToast({
        title: t(message.title),
        description: t(message.description),
      });
    },
  });

  const onSubmit = (data: LabelGroupFormSchema) => {
    const apiData = {
      name: data.name,
      notes: data.notes,
      labelIds: data.labels.map((label) => label.value),
    };

    submitData(apiData);
  };

  return (
    <article className="relative flex h-full flex-col">
      <SettingsPageHeader
        breadcrumbItems={[
          { to: SETTINGS_ROUTES.ROOT, label: 'title' },
          { to: SETTINGS_ROUTES.LABEL_GROUP_LIST, label: 'labelGroup.list' },
          { to: '', label: 'labelGroup.createTitle' },
        ]}
        title="labelGroup.createTitle"
      />
      <div className="grow overflow-y-auto pb-xl">
        <LabelGroupForm onSubmit={onSubmit} id={FORM_ID} />
      </div>
      <CTABlock>
        <Button asChild variant="secondary" size="large">
          <Link to={SETTINGS_ROUTES.LABEL_GROUP_LIST}>
            {t('buttons.cancel')}
          </Link>
        </Button>
        <Button
          variant="primary"
          type="submit"
          size="large"
          form={FORM_ID}
          disabled={isPending}
        >
          {t('labelGroup.addLink')}
        </Button>
      </CTABlock>
    </article>
  );
}

CreateLabelGroupPage.displayName = 'EditLabelGroupPage';
